import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Sparkles, Wand2, ImagePlus, Eye, ExternalLink } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Modal } from '@/components';
import { ProductSearchSelect } from '@/components/ProductSearchSelect';
import { pagesApi, PageFormData } from '@/services/pagesApi';
import { aiApi } from '@/services/aiApi';
import toast from 'react-hot-toast';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote', 'link'],
    ['clean'],
  ],
};

export const PageForm: React.FC = () => {
  const navigate = useNavigate();
  const { pageId } = useParams<{ pageId: string }>();
  const isEdit = !!pageId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState<'generate' | 'revise' | 'cover' | null>(null);
  const [revisionPrompt, setRevisionPrompt] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [isCoverPreviewOpen, setIsCoverPreviewOpen] = useState(false);
  const [form, setForm] = useState<PageFormData>({
    title: '',
    slug: '',
    content: '',
    type: 'quick-link',
    status: 'active',
    order: 0,
    excerpt: '',
    seoTitle: '',
    seoDescription: '',
    coverImageUrl: '',
    authorName: '',
    tags: [],
    productIds: [],
    aiPrompt: '',
  });

  const isBlog = form.type === 'blog';

  const updateForm = (updates: Partial<PageFormData>) => {
    setForm((current) => ({ ...current, ...updates }));
  };

  useEffect(() => {
    if (isEdit) {
      loadPage();
    }
  }, [pageId]);

  const loadPage = async () => {
    if (!pageId) return;
    try {
      setLoading(true);
      const page = await pagesApi.getById(pageId);
      setForm({
        title: page.title,
        slug: page.slug,
        content: page.content,
        type: page.type,
        status: page.status,
        order: page.order,
        excerpt: page.excerpt || '',
        seoTitle: page.seoTitle || '',
        seoDescription: page.seoDescription || '',
        coverImageUrl: page.coverImageUrl || '',
        authorName: page.authorName || '',
        tags: page.tags || [],
        productIds: page.productIds || [],
        aiPrompt: page.aiPrompt || '',
      });
      setTagsInput((page.tags || []).join(', '));
    } catch {
      toast.error('Sayfa yüklenemedi');
      navigate('/admin/pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Başlık zorunludur');
      return;
    }

    if (isBlog && !form.content.trim()) {
      toast.error('Blog içeriği boş olamaz');
      return;
    }

    try {
      setSaving(true);
      const payload: PageFormData = {
        ...form,
        tags: tagsInput
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      };

      if (isEdit) {
        await pagesApi.update(pageId!, payload);
        toast.success('Sayfa güncellendi');
      } else {
        await pagesApi.create(payload);
        toast.success('Sayfa oluşturuldu');
      }
      navigate('/admin/pages');
    } catch (error: any) {
      toast.error(error.message || 'Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateBlog = async () => {
    if (!form.aiPrompt?.trim()) {
      toast.error('Önce blog için parametre açıklamasını yazın');
      return;
    }

    try {
      setAiLoading('generate');
      const generated = await aiApi.generateBlogPost({
        prompt: form.aiPrompt,
        titleHint: form.title,
        currentDescription: form.excerpt,
        keywords: tagsInput
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        businessContext: 'TazeBiJest icin organik trafik getirecek, satin alma niyeti tasiyan kullanicilari hedefleyen SEO blog icerigi uret.',
      });

      updateForm({
        title: generated.title || form.title,
        slug: generated.suggestedSlug || form.slug,
        excerpt: generated.excerpt || form.excerpt,
        seoTitle: generated.seoTitle || form.seoTitle,
        seoDescription: generated.seoDescription || form.seoDescription,
        content: generated.content || form.content,
        authorName: form.authorName || 'TazeBiJest Editörü',
        status: 'inactive',
      });
      setTagsInput((generated.tags || []).join(', '));
      toast.success('AI blog taslağı oluşturdu');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'AI taslak oluşturamadı');
    } finally {
      setAiLoading(null);
    }
  };

  const handleReviseBlog = async () => {
    if (!revisionPrompt.trim()) {
      toast.error('Revizyon notu yazın');
      return;
    }

    if (!form.content.trim()) {
      toast.error('Revize edilecek bir blog taslağı gerekli');
      return;
    }

    try {
      setAiLoading('revise');
      const revised = await aiApi.reviseBlogPost({
        revisionPrompt,
        currentDraft: form.content,
        currentTitle: form.title,
        currentExcerpt: form.excerpt,
        currentSeoTitle: form.seoTitle,
        currentSeoDescription: form.seoDescription,
        keywords: tagsInput
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });

      updateForm({
        title: revised.title || form.title,
        excerpt: revised.excerpt || form.excerpt,
        seoTitle: revised.seoTitle || form.seoTitle,
        seoDescription: revised.seoDescription || form.seoDescription,
        content: revised.content || form.content,
      });
      if (revised.tags?.length) {
        setTagsInput(revised.tags.join(', '));
      }
      toast.success('Blog taslağı revize edildi');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'AI revizyon yapamadı');
    } finally {
      setAiLoading(null);
    }
  };

  const handleGenerateCover = async () => {
    if (!form.title.trim()) {
      toast.error('Önce blog başlığını yazın');
      return;
    }

    try {
      setAiLoading('cover');
      const result = await aiApi.generateBlogCoverImage({
        title: form.title,
        excerpt: form.excerpt,
        brief: form.aiPrompt,
        tags: tagsInput
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
      });

      updateForm({ coverImageUrl: result.url });
      toast.success('AI kapak görseli oluşturdu');
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'AI kapak görseli oluşturamadı');
    } finally {
      setAiLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin text-4xl">⏳</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/pages')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">
            {isEdit ? 'İçerik Düzenle' : 'Yeni İçerik'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEdit ? 'Sayfa veya blog içeriğini düzenleyin' : 'Footer sayfası ya da SEO blog yazısı oluşturun'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title */}
          <div className="card">
            <label className="block text-sm font-medium mb-2">Sayfa Başlığı</label>
            <input
              type="text"
              className="input-field"
              placeholder={isBlog ? 'Örn: Anneler Günü İçin En İyi Çiçek Seçimi' : 'Örn: Gizlilik Politikası'}
              value={form.title}
              onChange={(e) => updateForm({ title: e.target.value })}
            />
          </div>

          <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Slug</label>
              <input
                type="text"
                className="input-field"
                placeholder="otomatik-olusturulur"
                value={form.slug || ''}
                onChange={(e) => updateForm({ slug: e.target.value })}
              />
              <p className="text-xs text-gray-500 mt-1">Boş bırakırsanız başlıktan üretilir.</p>
            </div>

            {isBlog ? (
              <div>
                <label className="block text-sm font-medium mb-2">Yazar</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="TazeBiJest Editörü"
                  value={form.authorName || ''}
                  onChange={(e) => updateForm({ authorName: e.target.value })}
                />
              </div>
            ) : null}
          </div>

          {isBlog ? (
            <div className="card space-y-4 border border-rose-100 bg-rose-50/40 dark:bg-gray-900">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-dark dark:text-light">Gemini Blog Asistanı</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Sayfa açıklamasına yazdığınız parametrelerle SEO uyumlu blog taslağı üretir. Beğenirseniz yayına alır, not bırakırsanız revize eder.
                  </p>
                </div>
                <Sparkles className="text-rose-500" size={22} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Blog Parametreleri / Brief</label>
                <textarea
                  className="input-field min-h-[130px]"
                  placeholder="Hedef anahtar kelime, kullanıcı niyeti, özel gün, teslimat avantajı, şehir, ton, rakipten ayrışma gibi notları yazın."
                  value={form.aiPrompt || ''}
                  onChange={(e) => updateForm({ aiPrompt: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleGenerateBlog}
                  disabled={aiLoading !== null}
                  className="btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Sparkles size={18} />
                  {aiLoading === 'generate' ? 'Taslak Üretiliyor...' : 'AI ile Taslak Üret'}
                </button>

                <button
                  type="button"
                  onClick={handleReviseBlog}
                  disabled={aiLoading !== null}
                  className="btn flex items-center justify-center gap-2 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Wand2 size={18} />
                  {aiLoading === 'revise' ? 'Revize Ediliyor...' : 'AI ile Revize Et'}
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Revizyon Notu</label>
                <textarea
                  className="input-field min-h-[100px]"
                  placeholder="Örn: giriş kısmını daha kısa yap, Samsun teslimat vurgusunu artır, sonuç bölümüne CTA ekle."
                  value={revisionPrompt}
                  onChange={(e) => setRevisionPrompt(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          {isBlog ? (
            <div className="card grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Kısa Özet</label>
                <textarea
                  className="input-field min-h-[100px]"
                  placeholder="Blog liste sayfası ve meta alanları için kısa özet"
                  value={form.excerpt || ''}
                  onChange={(e) => updateForm({ excerpt: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">SEO Başlığı</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Google sonucunda görünecek başlık"
                  value={form.seoTitle || ''}
                  onChange={(e) => updateForm({ seoTitle: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Kapak Görseli URL</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="https://..."
                  value={form.coverImageUrl || ''}
                  onChange={(e) => updateForm({ coverImageUrl: e.target.value })}
                />
                <button
                  type="button"
                  onClick={handleGenerateCover}
                  disabled={aiLoading !== null}
                  className="mt-3 btn flex w-full items-center justify-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                >
                  <ImagePlus size={18} />
                  {aiLoading === 'cover' ? 'Kapak Görseli Üretiliyor...' : 'AI ile Kapak Görseli Üret'}
                </button>

                {form.coverImageUrl ? (
                  <div className="mt-3 overflow-hidden rounded-2xl border border-rose-100 bg-white">
                    <button
                      type="button"
                      onClick={() => setIsCoverPreviewOpen(true)}
                      className="block w-full cursor-zoom-in"
                    >
                      <img
                        src={form.coverImageUrl}
                        alt="Kapak görseli önizleme"
                        className="h-48 w-full object-cover"
                      />
                    </button>
                    <div className="flex gap-2 border-t border-rose-100 p-3">
                      <button
                        type="button"
                        onClick={() => setIsCoverPreviewOpen(true)}
                        className="btn flex flex-1 items-center justify-center gap-2 border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                      >
                        <Eye size={16} />
                        View Et
                      </button>
                      <a
                        href={form.coverImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn flex flex-1 items-center justify-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                      >
                        <ExternalLink size={16} />
                        Yeni Sekmede Aç
                      </a>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">SEO Meta Açıklaması</label>
                <textarea
                  className="input-field min-h-[90px]"
                  placeholder="Maksimum 155 karakterlik meta açıklama"
                  value={form.seoDescription || ''}
                  onChange={(e) => updateForm({ seoDescription: e.target.value })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Etiketler</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="anneler gunu, cicek onerileri, samsun cicek"
                  value={tagsInput}
                  onChange={(e) => setTagsInput(e.target.value)}
                />
              </div>
            </div>
          ) : null}

          {/* Content */}
          <div className="card">
            <label className="block text-sm font-medium mb-2">İçerik</label>
            <ReactQuill
              theme="snow"
              value={form.content}
              onChange={(content) => updateForm({ content })}
              modules={QUILL_MODULES}
              style={{ minHeight: '300px' }}
            />
          </div>

          {/* Product Selection for Blog */}
          {isBlog ? (
            <div className="card space-y-3">
              <div>
                <h3 className="text-lg font-semibold text-dark dark:text-light">Öne Çıkan Ürünler</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Blog yazısının altında ürün kartı olarak gösterilecek ürünleri seçin.
                </p>
              </div>
              <ProductSearchSelect
                selectedIds={form.productIds || []}
                onChange={(ids) => updateForm({ productIds: ids })}
              />
            </div>
          ) : null}
        </div>

        {/* Right: Settings */}
        <div className="space-y-6">
          <div className="card space-y-4">
            <h3 className="font-semibold text-dark dark:text-light">Ayarlar</h3>

            {isBlog ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-500">Toplam View</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{(form as any).totalViews || 0}</p>
                </div>
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-blue-500">Tekil View</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900">{(form as any).totalUniqueViews || 0}</p>
                </div>
              </div>
            ) : null}

            {form.coverImageUrl ? (
              <div className="overflow-hidden rounded-2xl border border-rose-100 bg-white">
                <img
                  src={form.coverImageUrl}
                  alt="Blog kapak önizleme"
                  className="h-56 w-full object-cover"
                />
                <div className="flex gap-2 border-t border-rose-100 p-3">
                  <button
                    type="button"
                    onClick={() => setIsCoverPreviewOpen(true)}
                    className="btn flex flex-1 items-center justify-center gap-2 border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                  >
                    <Eye size={16} />
                    Görseli Aç
                  </button>
                  <a
                    href={form.coverImageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn flex flex-1 items-center justify-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                  >
                    <ExternalLink size={16} />
                    Yeni Sekmede Aç
                  </a>
                </div>
              </div>
            ) : null}

            {/* Type */}
            <div>
              <label className="block text-sm font-medium mb-1">Sayfa Tipi</label>
              <select
                className="input-field"
                value={form.type}
                onChange={(e) => updateForm({ type: e.target.value as any })}
              >
                <option value="quick-link">Hızlı Link</option>
                <option value="support">Destek</option>
                <option value="blog">Blog Yazısı</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Blog yazıları SEO amaçlı /blog altında yayınlanır; diğerleri footer bağlantısı olur.
              </p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium mb-1">Durum</label>
              <select
                className="input-field"
                value={form.status}
                onChange={(e) => updateForm({ status: e.target.value as any })}
              >
                <option value="active">{isBlog ? 'Yayında' : 'Aktif'}</option>
                <option value="inactive">{isBlog ? 'Taslak' : 'Pasif'}</option>
              </select>
              {isBlog ? <p className="text-xs text-gray-500 mt-1">Taslak kaydedebilir, beğendiğinizde yayına alabilirsiniz.</p> : null}
            </div>

            {/* Order */}
            {!isBlog ? (
            <div>
              <label className="block text-sm font-medium mb-1">Sıralama</label>
              <input
                type="number"
                className="input-field"
                value={form.order}
                onChange={(e) => updateForm({ order: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
            ) : null}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Save size={20} />
            {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Oluştur'}
          </button>
        </div>
      </div>

      <Modal
        isOpen={isCoverPreviewOpen}
        title="Kapak Görseli Önizleme"
        onClose={() => setIsCoverPreviewOpen(false)}
        size="xl"
      >
        {form.coverImageUrl ? (
          <div className="space-y-4">
            <img
              src={form.coverImageUrl}
              alt="Kapak görseli büyük önizleme"
              className="max-h-[75vh] w-full rounded-xl bg-slate-50 object-contain"
            />
            <a
              href={form.coverImageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn flex w-full items-center justify-center gap-2 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
            >
              <ExternalLink size={16} />
              Yeni Sekmede Aç
            </a>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};
